import axios from "axios";
import tokenService from "@/services/TokenService";

const AuthenticationService = {
    clearLoginData: (store) => {
        store.commit('removeUserName');
        store.commit('removeUserToken');
        store.commit('removeRefreshToken');
        store.commit('removeSession');
    },
    callLogoutApi: async (store, router) => {
        await axios.post(process.env.VUE_APP_API_BASE_URL + '/api/cognito/logout', {}, tokenService.getConfig(router, store))
            .then(() => {
                AuthenticationService.clearLoginData(store);
                router.push('/login');
            })
            .catch(error => {
                console.error('There was an error!', error);
                AuthenticationService.clearLoginData(store);
                router.push('/login');
            });
    },
    logoutByTimeout: (router, store, hideMessage) => {
        if(!store) {
            router.push('/login');
            return;
        }

        AuthenticationService.clearLoginData(store);
        if (!hideMessage) {
            store.commit('setShowTokenExpiringMessage', 'Your session expired. Please login to use the system.');
        }

        router.push('/login');
    },
    disable: (store, router) => {
        store.commit('setUserToken', 'disabled');
        store.commit('setUserName', '');
        store.commit('setShowTokenExpiringMessage', '');
        router.push('/login');
    },
    checkIfResetPasswordRequired: async (email, password) => {
        return await axios.post(process.env.VUE_APP_API_BASE_URL + `/api/cognito/login`, {
            email: email,
            password: password,
            authChallengeType: 'none'
        }).then((response) => {
            if (response.data.ChallengeName && response.data.ChallengeName === "NEW_PASSWORD_REQUIRED") {
                return {
                    status: 'new_password_required'
                };
            }

            return {status: 'success'};
        }).catch((error) => {
            console.error('Failed to call login:', error.response);

            if (error.response.data.message === 'Incorrect username or password.') {
                return {
                    status: 'incorrect_username_or_password',
                };
            }

            return {
                status: 'error',
                message: error.response.data.message
            };
        });
    },
    loginCheck: async (email, password) => {
        try {
            const response = await axios.post(process.env.VUE_APP_API_BASE_URL + '/api/cognito/login/check',
                {
                    email: email,
                    password: password
                });

            return {
                status: 'success',
                message: response.data.message
            };
        } catch (error) {
            console.error('Failed to login:', error.response || error);
            return {
                status: 'error',
                message: error.response.data.message,
            };
        }
    },
    login: async (store, email, password, authChallengeType="email") => {
        try {
            const response = await axios.post(process.env.VUE_APP_API_BASE_URL + `/api/cognito/login`, {
                email: email,
                password: password,
                authChallengeType: authChallengeType
            });

            if (response.data.ChallengeName && response.data.ChallengeName === "NEW_PASSWORD_REQUIRED") {
                console.error('ChallengeName detected:', response.data.ChallengeName);
                return {status: 'new_password_required'};
            }

            store.commit('setSession', response.data.Session);

            return {status: 'success'};
        } catch (error) {
            console.error('Failed to login:', error.response || error);
            return {
                status: 'error',
                message: error.response.data.message
            };
        }
    },
    verifyMFACode: async (router, store, email, mfaCode, session) => {
        return await axios.post(process.env.VUE_APP_API_BASE_URL + `/api/cognito/login/verify`, {
            email: email,
            code: mfaCode,
            session: session,
        }).then(async (response) => {
            // Save the user token
            store.commit('setUserToken', response.data.access_token);
            store.commit('setRefreshToken', response.data.refresh_token);

            //set user name
            await AuthenticationService.getUserName(router, store);

            return {status: 'success'};
        }).catch((error) => {
            const errorMessage = error.response?.data?.message;
            console.error('Failed to verify MFA code:', errorMessage);
            //reset the session
            store.commit('removeSession');
            const newSession = error.response?.data?.Session ?? '';
            store.commit('setSession', newSession);
            return {status: 'error'};
        });
    },
    loginWithTempPassword: async function (router, store, email, tempPassword, newPassword, confirmPassword) {
        return await axios.post(process.env.VUE_APP_API_BASE_URL + `/api/cognito/login-by-temporary-password`, {
            email: email,
            temporary_password: tempPassword,
            new_password: newPassword,
            new_password_confirmation: confirmPassword,
        }).then(async (response) => {
            store.commit('setUserToken', response.data.access_token);
            store.commit('setRefreshToken', response.data.refresh_token);

            //set user name
            await AuthenticationService.getUserName(router, store);

            return {status: 'success'};
        }).catch((error) => {
            console.error('Failed to login with temporary password:', error.response || error);
            return {status: 'error'};
        });
    },
    requestResetPasswordCode: async function (email) {
        return await axios.post(process.env.VUE_APP_API_BASE_URL + `/api/cognito/forgot-password`, {
            email: email,
        }).then(() => {
            return {status: 'success'};
        }).catch((error) => {
            console.error('Failed to request reset password code:', error.response || error);
            return {status: 'error', message: error.response.data.message};
        });
    },
    forgetPasswordConfirm: async function (email, code, newPassword) {
        return await axios.post(process.env.VUE_APP_API_BASE_URL + `/api/cognito/forgot-password/confirm`, {
            email: email,
            code: code,
            password: newPassword,
            password_confirmation: newPassword,
        }).then(() => {
            return {status: 'success'};
        }).catch((error) => {
            console.error('Failed to confirm forgot password:', error.response || error);
            return {status: 'error', message: error.response.data.message};
        });
    },
    changePassword: async function (router, store, current_password, newPassword) {
        return await axios.patch(process.env.VUE_APP_API_BASE_URL + `/api/cognito/passwords/change`, {
            current_password: current_password,
            new_password: newPassword,
            new_password_confirmation: newPassword,
        }, tokenService.getConfig(router, store))
            .then(() => {
                return {status: 'success'};
            }).catch((error) => {
                console.error('Failed to change password:', error.response || error);
                return {status: 'error', message: error.response.data.message};
            });
    },
    getUserNumber: async (email) => {
        try {
            const response = await axios.post(process.env.VUE_APP_API_BASE_URL + '/api/get-phone-number', {
                email: email,
            });

            const userNumber = response.data.phone_number;
            return {
                status: 'success',
                userNumber: userNumber
            };
        } catch (error) {
            console.error('There was an error!', error);
            return {
                status: 'error',
                message: error.response ? error.response.data.message : 'An unexpected error occurred'
            };
        }
    },
    getUserName: async (router, store) => {
        await axios.get(process.env.VUE_APP_API_BASE_URL + '/api/accounts/username', tokenService.getConfig(router, store))
            .then(response => {
                const userName = response.data.username;
                store.commit('setUserName', userName);
            })
            .catch(error => {
                console.error('There was an error!', error);
            });
    },
    resendConfirmationCode: async (email) => {
        return await axios.post(process.env.VUE_APP_API_BASE_URL + `/api/cognito/signup/resend-confirmation-code`, {
            email: email,
        }).then(() => {
            return {status: 'success'};
        }).catch((error) => {
            console.error('Failed to resend confirmation code:', error.response || error);
            return {status: 'error', message: error.response.data.message};
        });
    },
    verifyConfirmationCode: async (email, code) => {
        return await axios.post(process.env.VUE_APP_API_BASE_URL + `/api/cognito/signup/confirm`, {
            email: email,
            code: code,
        }).then(() => {
            return {status: 'success'};
        }).catch((error) => {
            console.error('Failed to verify confirmation code:', error.response || error);
            return {status: 'error', message: error.response.data.message};
        });
    }
}

export default AuthenticationService;
